import { ReactNode } from "react";
import { MdClose, MdHelpOutline } from "react-icons/md";
import clsx from "clsx";
import Button from "../Button/Button";
import { useTranslation } from "react-i18next";
import ExpenserTriangle from "../ExpenserTriangle/ExpenserTriangle";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind, IButton } from "../Button/Button.types";

interface IModalHeader {
  title?: string;
  onClose: () => void;
  children?: ReactNode;
  subtitle?: string;
  isClosable?: boolean;
  helpLink?: string;
  buttons?: IButton[];
}

const ModalHeader: React.FC<IModalHeader> = ({
  title = "",
  onClose,
  children,
  subtitle = "",
  isClosable = true,
  helpLink = "",
  buttons = [],
}) => {
  const hasTitle = title || subtitle;
  const { t } = useTranslation(["common"]);

  return hasTitle ? (
    <div
      className={clsx([
        "flex",
        "flex-row",
        "items-center",
        {
          "justify-between": hasTitle,
          "justify-end": !hasTitle,
        },
        "relative",
        "mb-4 md:mb-4",
        "before:content-['']",
        "before:border-b-2",
        "before:border-b-[2px]",
        "before:w-1/2",
        "before:border-b-emerald-400",
        "dark:before:border-b-purple-400",
        "before:absolute",
        "before:bottom-0",
        "before:left-0",
        "before:p-6",
        "before:h-[2px]",
        "gap-6",
        "white",
        "px-4 md:pt-4 pt-4 pb-2",
        "md:p-4 md:pb-4",
        "after:content-['']",
        "after:h-[30px]",
        "after:-bottom-[30px]",
        "after:absolute",
        "after:left-0",
        "after:right-0",
        "after:bg-gradient-to-b",
        "after:dark:from-gray-800",
        "after:dark:via-gray-800",
        "after:dark:to-gray-800/20",
        "after:from-white",
        "after:via-white",
        "after:to-white/20",
        "after:z-1",
      ])}
    >
      {hasTitle && (
        <div
          className={clsx([
            "flex",
            "gap-1",
            "sm:gap-2",
            "items-center",
            "truncate",
          ])}
        >
          <ExpenserTriangle
            size={ICON_SIZES.MD}
            className={clsx(["text-red-600", "shrink-0"])}
          />
          <div
            className={clsx(["flex", "flex-col", "leading-none", "truncate"])}
          >
            <h2
              className={clsx([
                "text-xs",
                "md:text-sm",
                "font-medium",
                "leading-none",
              ])}
            >
              <div className={clsx(["truncate"])}>{title}</div>
            </h2>
            <div
              className={clsx([
                "text-xs",
                "leading-none",
                "text-red-500",
                "dark:text-cyan-400",
              ])}
            >
              eXpenser.net
            </div>
          </div>
        </div>
      )}
      <div className="z-[2]">
        <ButtonGroup
          buttons={[
            ...(buttons?.length ? buttons : []),
            {
              onClick: () => {},
              tooltip: "Ayuda",
              icon: <MdHelpOutline size={ICON_SIZES.SM} />,
              hide: !helpLink,
            },
            {
              onClick: () => {
                if (isClosable) {
                  onClose();
                }
              },
              icon: <MdClose size={ICON_SIZES.SM} />,
              tooltip: t("common:tooltips.close"),
              kind: EButtonKind.NORMAL,
              className: clsx([
                {
                  "pointer-events-none": !isClosable,
                  invisible: !isClosable,
                },
              ]),
            },
          ]}
        />
      </div>
    </div>
  ) : (
    <div className={clsx(["absolute", "top-10", "right-10"])}>
      <Button
        kind={EButtonKind.NORMAL}
        onClick={() => {
          if (isClosable) {
            onClose();
          }
        }}
        icon={<MdClose size={42} />}
        className={clsx([
          {
            "pointer-events-none": !isClosable,
            invisible: !isClosable,
          },
        ])}
      />
    </div>
  );
};

export default ModalHeader;
